import type {
  BriefingEmotion, BriefingFacts, BriefingItemDTO, BriefingKind, BriefingSection, BriefingSectionFacts, BriefingTone,
  ProactiveValue,
} from './assistant-proactive';
import { RULE_LABELS, type ProactiveRule } from './assistant-proactive';
import type { ISODate } from './common';

// The briefing card's strings (S3 §8.4–§8.6, L3 D125 ★, D128 ★). Pure: the API renders the DTO with them and the web
// reads the same overflow rule, so the two never disagree. Asia/Ho_Chi_Minh is UTC+7 all year (dates.ts
// BUSINESS_UTC_OFFSET), so an instant converts to its business day and time without a timezone database.

const OFFSET_MS = 7 * 3_600_000;
const DAY_MS = 86_400_000;

/** The business-local wall clock of an instant, as a UTC Date read with the getUTC* methods. */
function local(instant: string): Date {
  const ms = new Date(instant).getTime();
  if (!Number.isFinite(ms)) throw new Error(`not an instant: ${instant}`);
  return new Date(ms + OFFSET_MS);
}
const pad2 = (n: number): string => String(n).padStart(2, '0');
const isoOf = (d: Date): ISODate => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
const dateAt = (date: ISODate): Date => new Date(`${date}T00:00:00Z`);

/** The business day of an instant. */
export function businessDayOf(instant: string): ISODate {
  return isoOf(local(instant));
}

/** "Thứ 2" … "Thứ 7", "Chủ nhật". */
export function weekdayLabel(date: ISODate): string {
  const day = dateAt(date).getUTCDay();
  return day === 0 ? 'Chủ nhật' : `Thứ ${day + 1}`;
}
const lower = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
/** "dd/mm". */
const dayMonth = (date: ISODate): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;
/** "Thứ 6, 25/09". */
const dated = (date: ISODate): string => `${weekdayLabel(date)}, ${dayMonth(date)}`;
/** "HH:MM" local. */
const clock = (instant: string): string => {
  const d = local(instant);
  return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
};

/** The day a card is titled by (D124): a daily's period, a weekly's business day of composition. */
export function briefingDate(b: { kind: BriefingKind; period: string; composedAt: string }): ISODate {
  return b.kind === 'daily' ? b.period : businessDayOf(b.composedAt);
}

/** `current`: today's daily, or this week's weekly inside its window. */
export type BriefingTitleInput = { kind: BriefingKind; current: boolean; date: ISODate };

/** "Mây · Sáng nay", "Mây · Tổng kết tuần", otherwise "Mây · Thứ 6, 27/09": the reader's own persona name (S8 D9). */
export function briefingTitle(b: BriefingTitleInput, persona: string): string {
  if (b.current) return b.kind === 'daily' ? `${persona} · Sáng nay` : `${persona} · Tổng kết tuần`;
  return `${persona} · ${dated(b.date)}`;
}

/** "Thứ 2, 05/10 · soạn lúc 7:30" (mockup §1): the hour without its leading zero. */
export function briefingWhen(date: ISODate, composedAt: string): string {
  const d = local(composedAt);
  return `${dated(date)} · soạn lúc ${d.getUTCHours()}:${pad2(d.getUTCMinutes())}`;
}

/** What a heading reads beyond its section: `address` is `{gọi}`, `since` the facts' `since`, `today` the reader's
 *  business day. */
export type HeadingContext = { kind: BriefingKind; address: string; since: string | null; today: ISODate };

const total = (s: Pick<BriefingSectionFacts, 'totals'>, key: string): number => s.totals[key] ?? 0;

/** The "Thay đổi" heading's start: "hôm qua" when `since` was the day before today, else its weekday. */
function changesFrom(since: string | null, today: ISODate): string | null {
  if (since === null) return null;
  const from = businessDayOf(since);
  if (dateAt(today).getTime() - dateAt(from).getTime() === DAY_MS) return 'hôm qua';
  return lower(weekdayLabel(from));
}

/** S6 §10: the outside heading's word for each item kind. */
const EXTERNAL_WORDS: readonly (readonly [string, string])[] = [['event', 'lịch'], ['message', 'thư'], ['task', 'việc']];

/**
 * S9 §8.4 (P12): the daily `delegated` heading, whose rows are mine as the delegator needing attention ("Việc {gọi}
 * giao cần chú ý"), the cards waiting for my "Nhận" ("Việc chờ {gọi} nhận"), or both ("Việc giao").
 */
function delegatedDailyHeading(s: Pick<BriefingSectionFacts, 'totals'>, address: string): string {
  const [decide, risk, waiting] = [total(s, 'decide'), total(s, 'risk'), total(s, 'waiting')];
  const given = [decide > 0 ? `${decide} cần quyết` : null, risk > 0 ? `${risk} có rủi ro` : null]
    .filter((p): p is string => p !== null);
  if (given.length === 0) return `Việc chờ ${address} nhận · ${waiting}`;
  if (waiting === 0) return `Việc ${address} giao cần chú ý · ${given.join(', ')}`;
  return `Việc giao · ${[...given, `${waiting} chờ ${address} nhận`].join(', ')}`;
}

/** A group's heading (D125, mockup §1). */
export function groupHeading(s: Pick<BriefingSectionFacts, 'section' | 'count' | 'totals'>, ctx: HeadingContext): string {
  const n = s.count;
  switch (s.section) {
    case 'overdue': return `Quá hạn · ${n}`;
    case 'today': return `Hôm nay · ${n}`;
    case 'delegated': {
      if (s.totals.waiting !== undefined) return delegatedDailyHeading(s, ctx.address);
      const parts = [total(s, 'blocked') > 0 ? `${total(s, 'blocked')} bị chặn` : null,
        total(s, 'late') > 0 ? `${total(s, 'late')} trễ` : null].filter((p): p is string => p !== null);
      return `Việc ${ctx.address} giao · ${parts.length > 0 ? parts.join(', ') : n}`;
    }
    case 'changes': {
      if (ctx.kind === 'weekly') return `Thay đổi trong tuần · ${n}`;
      const from = changesFrom(ctx.since, ctx.today);
      return from === null ? `Thay đổi · ${n}` : `Thay đổi từ ${from} · ${n}`;
    }
    case 'nudges': return `Lời nhắc · ${n}`;
    case 'reminders': return ctx.kind === 'weekly' ? `Lịch nhắc tuần tới · ${n}` : `Lịch nhắc · ${n}`;
    case 'projects': return `Dự án · ${n}`;
    case 'finance': return ctx.kind === 'weekly' ? 'Tài chính tuần này' : `Tài chính · ${n} khoản đến hạn`;
    case 'strategy': return `Chiến lược · ${n} cần xem`;
    case 'week': return 'Tuần qua và tuần tới';
    case 'external': {                                                  // S6 §10: the kinds, never a title
      const parts = EXTERNAL_WORDS.filter(([key]) => total(s, key) > 0).map(([key, word]) => `${total(s, key)} ${word}`);
      return `Hôm nay ngoài hệ thống · ${parts.length > 0 ? parts.join(', ') : n}`;
    }
  }
}

export type BriefingHint = { hint: string; tone: BriefingTone };
const num = (v: ProactiveValue | undefined): number => (typeof v === 'number' ? v : 0);
const late = (days: ProactiveValue | undefined): BriefingHint => ({ hint: `trễ ${num(days)} ngày`, tone: 'late' });

const CHANGE_HINTS: Record<string, BriefingHint> = {
  assigned: { hint: 'mới giao', tone: 'info' },
  delegatedDone: { hint: 'đã xong', tone: 'ok' },
  blocked: { hint: 'bị chặn', tone: 'late' },
  unblocked: { hint: 'gỡ chặn', tone: 'ok' },
  comment: { hint: 'bình luận mới', tone: 'info' },
};
const STRATEGY_HINTS: Record<string, string> = { off: 'lệch', noData: 'thiếu số liệu', noReview: 'thiếu review' };
/** A nudge row names its rule; a person's own reminder and watch have no rule label. */
const nudgeHint = (rule: ProactiveValue | undefined): string => {
  if (rule === 'user.reminder') return 'Lịch nhắc';
  if (rule === 'user.watch') return 'Theo dõi';
  return typeof rule === 'string' && rule in RULE_LABELS ? RULE_LABELS[rule as ProactiveRule] : 'Lời nhắc';
};

/**
 * A chip row's hint and tone (D125), from the stored fact values of its section (D116): `overdue` {daysLate},
 * `delegated` {blocked, daysLate} (weekly) or, daily (S9 §8.4), {side, group} / {side: 'assignee', waiting}, `changes` {event}, `nudges` {severity, rule}, `reminders` {at}, `projects` daily
 * {rule, days} / weekly {done, opened, idleDays}, `finance` daily {daysLate} / weekly {overBudget}, `strategy`
 * {issue: off|noData|noReview}, `week` {done} or {due}.
 */
export function rowHint(section: BriefingSection, kind: BriefingKind,
  v: Readonly<Record<string, ProactiveValue>>): BriefingHint {
  switch (section) {
    case 'overdue': return late(v.daysLate);
    case 'today': return { hint: 'hôm nay', tone: 'warn' };
    case 'delegated':
      if (v.side === 'assignee') return { hint: 'chờ nhận', tone: 'warn' };   // D76
      if (v.group === 'decide') return { hint: 'cần quyết', tone: 'warn' };
      if (v.group === 'risk') return { hint: 'có rủi ro', tone: 'late' };
      return v.blocked === true ? { hint: 'bị chặn', tone: 'late' } : late(v.daysLate);
    case 'changes': return CHANGE_HINTS[String(v.event)] ?? { hint: 'thay đổi', tone: 'info' };
    case 'nudges': return { hint: nudgeHint(v.rule), tone: v.severity === 'high' ? 'late' : 'warn' };
    case 'reminders': {
      const at = typeof v.at === 'string' ? v.at : null;
      if (at === null) return { hint: '', tone: 'info' };
      return { hint: kind === 'weekly' ? `${weekdayLabel(businessDayOf(at))}, ${clock(at)}` : clock(at), tone: 'info' };
    }
    case 'projects':
      if (kind === 'daily') {
        return v.rule === 'project.ending' ? { hint: 'sắp kết thúc', tone: 'warn' }
          : { hint: `đứng im ${num(v.days)} ngày`, tone: 'warn' };
      }
      return typeof v.idleDays === 'number' ? { hint: `đứng im ${v.idleDays} ngày`, tone: 'warn' }
        : { hint: `xong ${num(v.done)} · mở ${num(v.opened)}`, tone: 'info' };
    case 'finance':
      if (kind === 'weekly' || v.overBudget === true) return { hint: 'vượt ngân sách', tone: 'late' };
      return num(v.daysLate) > 0 ? late(v.daysLate) : { hint: 'đến hạn hôm nay', tone: 'warn' };
    case 'strategy': return { hint: STRATEGY_HINTS[String(v.issue)] ?? 'cần xem', tone: 'warn' };
    case 'week':
      return typeof v.due === 'string' ? { hint: dated(v.due), tone: 'info' } : { hint: 'đã xong', tone: 'ok' };
    // S6 §10: an outside item's business clock; a date alone (an all-day event, a task's due day) shows none.
    case 'external':
      return typeof v.at === 'string' && v.at.includes('T') ? { hint: clock(v.at), tone: 'info' }
        : { hint: '', tone: 'info' };
  }
}

/** The existing `viNumber` format (templates.ts). */
const viNumber = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });

/** A row with no chip, with the raw numbers it prints: emptiness reads `values`, never the formatted `item.hint`
 *  (D140 — 0.04 đ prints "0"). */
export interface BriefingTotalRow { item: BriefingItemDTO; values: readonly number[] }

/** The rows with no chip a weekly section carries (D125), from its `totals`: finance actual / planned, the week's
 *  counts. Every other section, and every daily, has none. */
export function totalRows(section: BriefingSection, kind: BriefingKind,
  totals: Readonly<Record<string, number>>): BriefingTotalRow[] {
  if (kind !== 'weekly') return [];
  const t = (key: string): number => totals[key] ?? 0;
  if (section === 'finance') {
    const money = (label: string, actual: string, planned: string): BriefingTotalRow => ({
      item: { ref: null, label, hint: `${viNumber.format(t(actual))} / ${viNumber.format(t(planned))} đ`, tone: 'info' },
      values: [t(actual), t(planned)] });
    return [money('Thu tuần này', 'incomeActual', 'incomePlanned'),
      money('Chi tuần này', 'expenseActual', 'expensePlanned')];
  }
  if (section === 'week') {
    const count = (label: string, key: string, tone: BriefingItemDTO['tone']): BriefingTotalRow => ({
      item: { ref: null, label, hint: viNumber.format(t(key)), tone }, values: [t(key)] });
    return [count('Việc đã xong tuần qua', 'done', 'ok'), count('Việc giao đi đã xong', 'delegatedDone', 'ok'),
      count('Việc hạn tuần tới', 'next', 'info')];
  }
  return [];
}

/** A group is left out of the DTO iff it has no chip (`count` 0) AND every raw total its rows print is 0 (D140). */
export function groupIsEmpty(count: number, totals: readonly BriefingTotalRow[]): boolean {
  return count === 0 && totals.every((r) => r.values.every((v) => v === 0));
}

/**
 * A group's overflow (D125): up to 3 rows show whole; past 3, `initial` 2 rows and the "+ n việc nữa" button (n =
 * count − 2) that reveals the rest of the DTO's rows; past the DTO's rows (≤10), a muted "+ k việc khác" line.
 * `noun` is what the rows are counted in (`overflowNoun`).
 */
export function overflowOf(count: number, rows: number,
  noun = 'việc'): { initial: number; more: string | null; rest: string | null } {
  const rest = count > rows ? `+ ${count - rows} ${noun} khác` : null;
  if (count <= 3) return { initial: Math.min(count, rows), more: null, rest };
  return { initial: Math.min(2, rows), more: `+ ${count - 2} ${noun} nữa`, rest };
}

/** The noun a group's overflow counts in: "mục" for the outside items (S6 §10), else "việc". */
export const overflowNoun = (section: BriefingSection): string => (section === 'external' ? 'mục' : 'việc');

const sectionOf = (f: BriefingFacts, section: BriefingSection): BriefingSectionFacts | undefined =>
  f.sections.find((s) => s.section === section);
const countOf = (f: BriefingFacts, section: BriefingSection): number => sectionOf(f, section)?.count ?? 0;
const totalOf = (f: BriefingFacts, section: BriefingSection, key: string): number =>
  sectionOf(f, section)?.totals[key] ?? 0;

/** The line a seen card collapses to: "Hana · Sáng nay · 4 quá hạn, 3 hôm nay" (zero parts dropped, none left →
 *  "không có việc gấp"); a weekly "… · a việc đã xong, b việc tuần tới". */
export function collapsedLine(title: string, f: BriefingFacts): string {
  if (f.kind === 'weekly') {
    return `${title} · ${totalOf(f, 'week', 'done')} việc đã xong, ${totalOf(f, 'week', 'next')} việc tuần tới`;
  }
  const parts = [countOf(f, 'overdue') > 0 ? `${countOf(f, 'overdue')} quá hạn` : null,
    countOf(f, 'today') > 0 ? `${countOf(f, 'today')} hôm nay` : null].filter((p): p is string => p !== null);
  return `${title} · ${parts.length > 0 ? parts.join(', ') : 'không có việc gấp'}`;
}

/** §8.4: worried on an overdue task or a high nudge; proud with nothing overdue and no late delegated task (the
 *  delegator's rows only: a card waiting for my "Nhận" is never late work I gave away, S9 §8.4); else greet. */
export function emotionOf(f: BriefingFacts): BriefingEmotion {
  if (countOf(f, 'overdue') > 0 || totalOf(f, 'nudges', 'high') > 0) return 'worried';
  return totalOf(f, 'delegated', 'late') === 0 ? 'proud' : 'greet';
}

/** "Hỏi Hana thêm"'s first message (D128 ★). */
export function briefingAskText(b: BriefingTitleInput): string {
  if (b.kind === 'weekly') return 'Kể thêm về bản tổng kết tuần';
  return b.current ? 'Kể thêm về bản tóm tắt sáng nay' : `Kể thêm về bản tóm tắt ${lower(dated(b.date))}`;
}
